import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAuth from "../../../hooks/auth/useAuth";
import { UseReactQueryPropsBase } from "../../../api/ReactQueryClient";
import { IsMobileAppModalShown, UserRole } from "../../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import {
  HOME_ROUTE,
  ADMIN_USERS_ROUTE,
  COACH_PROGRAMS_PUBLISHED_ROUTE,
  COACH_PLAN_ROUTE,
  COACH_CLIENT_PORTAL_ROUTE,
  COACH_THEME_ROUTE,
  COACH_BRANDED_APP_ROUTE,
  COACH_FEATURES_SETTINGS_ROUTE,
} from "../../../routes/routes";

export const useSignInMutation = (props?: UseReactQueryPropsBase) => {
  localStorage.removeItem(IsMobileAppModalShown);
  const { login } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["signIn"],
    mutationFn: login,
    retry: 0,
    ...props,
    onSuccess: async (user) => {
      // Important: Invalidate all queries to ensure the new user's data is fetched
      queryClient.clear();

      props?.onSuccess && props.onSuccess(user);
    },
  });
};

interface SignInAsMutationProps extends UseReactQueryPropsBase {
  redirect?: boolean;
  wasImpersonating?: boolean;
  switchingToCoach?: boolean;
  switchingFromSample?: boolean;
}

// Routes that should not be preserved when switching to another coach
const OWNER_ONLY_ROUTES = [
  COACH_PLAN_ROUTE,
  COACH_CLIENT_PORTAL_ROUTE,
  COACH_THEME_ROUTE,
  COACH_BRANDED_APP_ROUTE,
  COACH_FEATURES_SETTINGS_ROUTE,
];

export const useSignInAsMutation = (props?: SignInAsMutationProps) => {
  const { loginAs } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    redirect = true,
    wasImpersonating,
    switchingToCoach,
    switchingFromSample,
  } = props || {};

  return useMutation({
    mutationKey: ["signInAs"],
    mutationFn: loginAs,
    retry: 0,
    ...props,
    onSuccess: async (user) => {
      if (redirect) {
        const { userRole, admin } = user;

        const currentPath = location.pathname;
        const isRestrictedRoute = OWNER_ONLY_ROUTES.some((route) =>
          currentPath.startsWith(route),
        );

        const shouldPreserveUrl =
          switchingToCoach && !isRestrictedRoute && !switchingFromSample;

        if (!shouldPreserveUrl) {
          navigate(
            userRole === UserRole.CLIENT
              ? HOME_ROUTE
              : admin && wasImpersonating
                ? ADMIN_USERS_ROUTE
                : COACH_PROGRAMS_PUBLISHED_ROUTE,
          );
        }
      }

      // Important: Clear all queries to ensure the new user's data is fetched
      queryClient.clear();

      props?.onSuccess && props.onSuccess(user);
    },
  });
};
